import type { MaybeRef } from 'vue'
import { useQuoteStore } from '@store/quote'
import type { AddressCheckResult } from '@/utils/address-validation'
import { areAddressesUnique, isDisallowedCountry } from '@/utils/address-validation'
import { TypeEnum } from '@apiTypes'
import type { BlankEnum, VehicleTypeEnum, Waypoint } from '@apiTypes'

export function useAddressValidator() {
  const result = ref<AddressCheckResult>({
    pickup: { errors: [], warnings: [] },
    delivery: { errors: [], warnings: [] },
    global: { errors: [], warnings: [] },
  })

  const { t } = useI18n()
  const quoteStore = useQuoteStore()

  const formatPostcodeValidationText = (type: TypeEnum, area: string) => {
    return t('validation.postcodeWarning',
      {
        loadType: type === TypeEnum.Pickup ? t('term.load') : t('term.unload'),
        area: area === 'country' ? t('term.country') : t('term.region'),
      })
  }

  const formatAddressLookupValidationText = (type: TypeEnum) => {
    if (type === TypeEnum.Pickup)
      return t('validation.addressLookupIssue.pickup')

    return t('validation.addressLookupIssue.delivery')
  }

  const isAddressComplete = (address: Waypoint | undefined): address is Waypoint => {
    return Boolean(address?.country && address?.city)
  }

  const validateAddress = (address: Waypoint | undefined, type: TypeEnum) => {
    if (!isAddressComplete(address)) {
      result.value[type].errors.push(formatAddressLookupValidationText(type))

      return
    }

    if (isDisallowedCountry(address)) {
      const text = formatPostcodeValidationText(type, 'country') // Pass 'country' as the area

      result.value[type].warnings.push(text)
    }
  }

  const validateUniqueness = (pickupAddress: Waypoint | undefined, deliveryAddress: Waypoint | undefined) => {
    if (!areAddressesUnique(pickupAddress, deliveryAddress))
      result.value.global.errors.push(t('Addresses cannot be the same.'))
  }

  async function validatePostcodes(vehicleType: VehicleTypeEnum | BlankEnum | undefined, pickupAddress: Waypoint | undefined, deliveryAddress: Waypoint | undefined) {
    if (!isAddressComplete(pickupAddress) || !isAddressComplete(deliveryAddress))
      return

    // The API refuses a check without a vehicle type, and that refusal adds no warning either.
    if (!vehicleType)
      return

    try {
      await quoteStore.validateWaypoints(vehicleType, pickupAddress, deliveryAddress)
    }
    catch (error: unknown) {
      const responseData = (error as { response?: { data?: Record<string, unknown> } })?.response?.data

      for (const item of Object.values(responseData || {})) {
        switch (item) {
          case 'pickup_country': {
            result.value.pickup.warnings.push(formatPostcodeValidationText(TypeEnum.Pickup, 'country'))
            break
          }
          case 'pickup_postcode': {
            result.value.pickup.warnings.push(formatPostcodeValidationText(TypeEnum.Pickup, 'region'))
            break
          }
          case 'delivery_country': {
            result.value.delivery.warnings.push(formatPostcodeValidationText(TypeEnum.Delivery, 'country'))
            break
          }
          case 'delivery_postcode': {
            result.value.delivery.warnings.push(formatPostcodeValidationText(TypeEnum.Delivery, 'region'))
            break
          }
        }
      }
    }
  }

  const validate = async (vehicleTypeRef: MaybeRef<VehicleTypeEnum | BlankEnum | undefined>, quoteRef: MaybeRef<{ waypoints?: Waypoint[] }>) => {
    const quote = unref(quoteRef)
    const vehicleType = unref(vehicleTypeRef)

    const pickupAddress = quote.waypoints?.find(wp => wp.type === TypeEnum.Pickup)
    const deliveryAddress = quote.waypoints?.find(wp => wp.type === TypeEnum.Delivery)

    result.value = {
      pickup: { errors: [], warnings: [] },
      delivery: { errors: [], warnings: [] },
      global: { errors: [], warnings: [] },
    }

    validateAddress(pickupAddress, TypeEnum.Pickup)
    validateAddress(deliveryAddress, TypeEnum.Delivery)
    validateUniqueness(pickupAddress, deliveryAddress)

    await validatePostcodes(vehicleType, pickupAddress, deliveryAddress)

    return result
  }

  const hasIssues = computed(() => {
    return (
      result.value.pickup.errors.length > 0
      || result.value.pickup.warnings.length > 0
      || result.value.delivery.errors.length > 0
      || result.value.delivery.warnings.length > 0
      || result.value.global.errors.length > 0
      || result.value.global.warnings.length > 0
    )
  })

  return { validate, hasIssues }
}
